import React from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import Facebook from '@/assets/socialMedia/Facebook.svg?react'
import Instagram from '@/assets/socialMedia/Instagram.svg?react'
import Linkedln from '@/assets/socialMedia/LinkedIn.svg?react'
import Pinterest from '@/assets/socialMedia/Pinterest.svg?react'
import Reddit from '@/assets/socialMedia/Reddit.svg?react'
import TikTok from '@/assets/socialMedia/TikTok.svg?react'
import X from '@/assets/socialMedia/X.svg?react'
import { Divider } from '@/components/Divider/Divider.component'
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { buildSocialMediaUrl } from '@/utils/buildSocialMediaUrl'
import { SocialMirrorPalette } from '@/utils/palette/SocialMirrorPalette'
import { sanitizeSocialMediaUrl } from '@/utils/sanitizeSocialMediaUrl'

const socialMediaPlatforms = [
  {
    formField: 'linkedInUrl',
    label: 'LinkedIn',
    icon: Linkedln,
  },
  {
    formField: 'instagramUrl',
    label: 'Instagram',
    icon: Instagram,
  },
  {
    formField: 'twitterUrl',
    label: 'X (Twitter)',
    icon: X,
  },
  {
    formField: 'facebookUrl',
    label: 'Facebook',
    icon: Facebook,
  },
  {
    formField: 'tikTokUrl',
    label: 'TikTok',
    icon: TikTok,
  },
  {
    formField: 'pinterestUrl',
    label: 'Pinterest',
    icon: Pinterest,
  },
  {
    formField: 'redditUrl',
    label: 'Reddit',
    icon: Reddit,
  },
]

export const SocialMediaInputs: React.FC = () => {
  const { t } = useTranslation()
  const { control, formState, trigger, watch } = useFormContext()
  const { errors, touchedFields } = formState

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="grid grid-cols-1 gap-6 w-full">
        {socialMediaPlatforms.map((platform) => {
          const IconComponent = platform.icon
          const watchedValue = watch(platform.formField)

          return (
            <FormField
              key={platform.formField}
              name={platform.formField}
              control={control}
              render={({ field }) => {
                const hasError = errors[platform.formField]
                const hasValue = watchedValue && watchedValue.trim() !== ''
                const isValid = hasValue && !hasError

                return (
                  <>
                    <FormItem className="w-full rounded-lg">
                      <span className="text-sm">{platform.label}</span>

                      <div className="flex flex-row gap-4 items-center w-full">
                        {/* Input Field */}
                        <div className="flex flex-col gap-2 flex-1 min-w-0">
                          <FormControl>
                            <Input
                              {...field}
                              onBlur={(e) => {
                                const input = e.target.value

                                const builtUrl = buildSocialMediaUrl(
                                  input,
                                  platform.formField as any,
                                )

                                const sanitized =
                                  sanitizeSocialMediaUrl(builtUrl)

                                field.onChange(sanitized)
                                field.onBlur()
                                trigger(platform.formField)
                              }}
                              placeholder={t(`setup.searchForm.placeholder`)}
                              className="w-full rounded-full"
                            />
                          </FormControl>
                        </div>
                        <div
                          className={`flex items-center justify-center flex-shrink-0 rounded-full p-2 h-12 w-12 ${
                            isValid
                              ? 'bg-primaryColor30 border-primaryColor'
                              : 'bg-neutralColor30 border-none'
                          }`}
                        >
                          {/* Icon */}
                          <IconComponent
                            width={16}
                            height={16}
                            color={
                              isValid
                                ? SocialMirrorPalette.neutralColor
                                : SocialMirrorPalette.neutralColor80
                            }
                          />
                        </div>
                      </div>

                      <FormMessage data-name={platform.formField} />
                    </FormItem>
                    <Divider />
                  </>
                )
              }}
            />
          )
        })}
      </div>

      {/* Global Social Media Validation Error */}
      {errors.socialMedia && (
        <div className="text-red-500 text-sm mt-2">
          {errors.socialMedia.message as string}
        </div>
      )}
    </div>
  )
}
